import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

// The light entry point: the package root would pull in the charts.
import { DEFAULT_PAGE_SIZE, Pagination, needsPager, useClientPage } from "@strat/ui/pagination";
import { TableExportMenu, cellFromDisplay, nodeText } from "@strat/ui/export";
import type { ExportCell } from "@strat/ui/export";
import { useTablePrefs } from "@strat/ui/prefs";

/**
 * A table whose every column sorts, client-side, on click.
 *
 * Each column says how to render a cell and what value to sort it by; numbers
 * sort numerically with blanks last whichever way you sort, strings sort
 * naturally. The header carries `aria-sort`, and the sort key is a real
 * <button>, so it works from the keyboard and reads right to a screen reader.
 *
 * Given a `tableId`, the table remembers its sort and which columns are hidden
 * (Settings → Tables), and offers a "Columns" picker. The first column is the
 * row's identity and always stays.
 *
 * Long tables page: 20 rows by default, 10, 20 or 50 to choose, remembered
 * with the rest of the table's settings. Sorting works on every row, then
 * the page is cut, so page one of "biggest return first" is the real top 20.
 *
 * Every table offers "Export": all its rows, in the order shown, with the
 * columns shown, as CSV or Excel. A cell exports as the number it displays,
 * or the time it sorts by, unless its column says otherwise (`exportValue`).
 */
export interface Column<T> {
  key: string;
  label: ReactNode;
  render: (row: T) => ReactNode;
  sortValue?: (row: T) => number | string | null | undefined;
  align?: "left" | "right";
  /** The direction a first click sorts in. Numbers usually want biggest first. */
  firstDirection?: "asc" | "desc";
  title?: string;
  /** The cell in a CSV / Excel export, when what the screen shows is not it. */
  exportValue?: (row: T) => ExportCell;
}

interface Props<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  initialSort?: { key: string; direction: "asc" | "desc" };
  empty?: ReactNode;
  /**
   * The rows are still on their way. Shows placeholder rows rather than the
   * empty state, which would otherwise flash "No strategies yet" at someone
   * who has fourteen.
   */
  loading?: boolean;
  onRowClick?: (row: T) => void;
  caption?: string;
  /** Remember sort and hidden columns under this id, and offer a column picker. */
  tableId?: string;
  /** Rows per page to start with; null shows every row (a short list that never grows). */
  pageSize?: number | null;
  /** Names the exported file ("trades", "BTCUSDT"…); the table id or caption when not given. Null hides Export. */
  exportName?: string | (string | null | undefined)[] | null;
}

function compare(a: unknown, b: unknown): number {
  const blankA = a == null || a === "" || (typeof a === "number" && !Number.isFinite(a));
  const blankB = b == null || b === "" || (typeof b === "number" && !Number.isFinite(b));
  if (blankA || blankB) return blankA === blankB ? 0 : blankA ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function SortableTable<T>({
  rows,
  columns: allColumns,
  rowKey,
  initialSort,
  empty,
  loading = false,
  onRowClick,
  caption,
  tableId,
  pageSize = DEFAULT_PAGE_SIZE,
  exportName,
}: Props<T>) {
  const { table, set: saveTable } = useTablePrefs(tableId);
  const [sort, setSortState] = useState(
    (tableId && table.sort && allColumns.some((c) => c.key === table.sort!.key) ? table.sort : null) ??
      initialSort ??
      null,
  );
  const setSort = (next: (s: typeof sort) => typeof sort) =>
    setSortState((s) => {
      const value = next(s);
      if (tableId && value) saveTable({ sort: value });
      return value;
    });
  const hidden = new Set(tableId ? (table.hidden ?? []) : []);
  const columns = allColumns.filter((c, i) => i === 0 || !hidden.has(c.key));

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const value = col.sortValue;
    return [...rows].sort((x, y) => {
      const a = value(x);
      const b = value(y);
      const blank = (v: unknown) => v == null || v === "" || (typeof v === "number" && !Number.isFinite(v));
      // Blanks last in both directions: "no data" is never the best or the worst.
      if (blank(a) || blank(b)) return compare(a, b);
      return compare(a, b) * dir;
    });
  }, [rows, columns, sort]);

  const page = useClientPage(sorted, {
    initialLimit: pageSize ?? undefined,
    limit: table.pageSize,
    onLimitChange: (next) => tableId && saveTable({ pageSize: next }),
    resetKey: sort ? `${sort.key}:${sort.direction}` : "",
  });
  const paged = pageSize !== null && needsPager(sorted.length);
  const visible = paged ? page.pageItems : sorted;

  const placeholder = loading && rows.length === 0;
  if (rows.length === 0 && empty && !placeholder) return <>{empty}</>;

  const fileName =
    exportName === null
      ? null
      : Array.isArray(exportName)
        ? exportName
        : [exportName ?? caption ?? tableId ?? "table"];
  const exportData = () => ({
    headers: columns.map((c) => columnName(c)),
    rows: sorted.map((row) =>
      columns.map((c) =>
        c.exportValue ? c.exportValue(row) : cellFromDisplay(c.render(row), c.sortValue?.(row)),
      ),
    ),
  });

  return (
    <>
      {(tableId || (fileName && rows.length > 0)) && (
        <div className="table-tools">
          {fileName && rows.length > 0 && <TableExportMenu data={exportData} name={fileName} />}
          {tableId && (
            <ColumnPicker
              columns={allColumns}
              hidden={hidden}
              onChange={(next) => saveTable({ hidden: [...next] })}
            />
          )}
        </div>
      )}
      <div className="table-wrap" aria-busy={placeholder || undefined}>
        <table className="sortable">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                const ariaSort = active ? (sort!.direction === "asc" ? "ascending" : "descending") : "none";
                return (
                  <th
                    key={c.key}
                    aria-sort={c.sortValue ? ariaSort : undefined}
                    className={c.align === "right" ? "num" : undefined}
                    title={c.title}
                  >
                    {c.sortValue ? (
                      <button
                        type="button"
                        className={`sort-head${active ? " is-active" : ""}`}
                        onClick={() =>
                          setSort((s) =>
                            s?.key === c.key
                              ? { key: c.key, direction: s.direction === "asc" ? "desc" : "asc" }
                              : { key: c.key, direction: c.firstDirection ?? "desc" },
                          )
                        }
                      >
                        {c.label}
                        <span className="sort-head__mark" aria-hidden="true">
                          {active ? (sort!.direction === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {placeholder &&
              [0, 1, 2].map((i) => (
                <tr key={`placeholder-${i}`} className="is-placeholder" aria-hidden="true">
                  {columns.map((c) => (
                    <td key={c.key}>
                      <span className="skeleton" />
                    </td>
                  ))}
                </tr>
              ))}
            {visible.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? "is-clickable" : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "num" : undefined}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paged && <Pagination {...page.pager} />}
    </>
  );
}

/** The text a column goes by in the picker: its label when that is text. */
function columnName<T>(c: Column<T>): string {
  if (typeof c.label === "string" && c.label.trim()) return c.label;
  return nodeText(c.label)?.trim() || c.title || c.key;
}

/** "Columns ▾": tick the columns this table shows. Saved with the person's preferences. */
function ColumnPicker<T>({
  columns,
  hidden,
  onChange,
}: {
  columns: Column<T>[];
  hidden: Set<string>;
  onChange: (hidden: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node))
        setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);
  const shown = columns.length - columns.slice(1).filter((c) => hidden.has(c.key)).length;
  return (
    <div className="column-picker" ref={ref}>
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Columns {shown}/{columns.length} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="column-picker__list" role="group" aria-label="Columns to show">
          {columns.map((c, i) => (
            <label key={c.key} className="checkbox">
              <input
                type="checkbox"
                checked={i === 0 || !hidden.has(c.key)}
                disabled={i === 0}
                onChange={(e) => {
                  const next = new Set(hidden);
                  if (e.target.checked) next.delete(c.key);
                  else next.add(c.key);
                  onChange(next);
                }}
              />
              {columnName(c)}
            </label>
          ))}
          {hidden.size > 0 && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => onChange(new Set())}>
              Show all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
